import Autocomplete from '@mui/material/Autocomplete';
import OutlinedInput from '@mui/material/OutlinedInput';
import type { ReactNode } from 'react';

import { type FieldProps, FormField, splitFieldProps, useFormField } from './FormField';
import { MUI_SIZE } from './sizes';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxBase extends FieldProps {
  options: readonly ComboboxOption[];
  placeholder?: string;
  noOptionsText?: ReactNode;
  loading?: boolean;
  onBlur?: () => void;
}

interface SingleCombobox extends ComboboxBase {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
}

interface MultipleCombobox extends ComboboxBase {
  /** Several values, shown as chips; the field grows a row at a time. */
  multiple: true;
  value: readonly string[];
  onChange: (value: string[]) => void;
}

export type ComboboxProps = SingleCombobox | MultipleCombobox;

type ControlProps =
  Omit<SingleCombobox, keyof FieldProps> | Omit<MultipleCombobox, keyof FieldProps>;

function ComboboxControl(props: ControlProps) {
  const field = useFormField();
  const { options, placeholder, noOptionsText, loading, onBlur } = props;
  const byValue = new Map(options.map((option) => [option.value, option]));

  return (
    <Autocomplete<ComboboxOption, boolean, false, false>
      id={field.controlId}
      multiple={props.multiple ?? false}
      options={options}
      value={
        props.multiple
          ? props.value.flatMap((value) => byValue.get(value) ?? [])
          : (byValue.get(props.value ?? '') ?? null)
      }
      onChange={(_event, selected) => {
        if (props.multiple) {
          props.onChange(((selected ?? []) as ComboboxOption[]).map((option) => option.value));
        } else {
          props.onChange((selected as ComboboxOption | null)?.value ?? null);
        }
      }}
      onBlur={onBlur}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionLabel={(option) => option.label}
      size={MUI_SIZE[field.size]}
      disabled={field.disabled}
      loading={loading}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <OutlinedInput
          {...params.slotProps.input}
          inputProps={{ ...params.slotProps.htmlInput, 'aria-describedby': field.describedBy }}
          fullWidth={params.fullWidth}
          placeholder={placeholder}
        />
      )}
    />
  );
}

/**
 * A searchable list (single choice, or several shown as chips) with label, hint and error. One
 * row is as tall as the other controls of its size; chips wrap onto more rows.
 */
export function Combobox(props: ComboboxProps) {
  const { field, control } = splitFieldProps(props);
  return (
    <FormField {...field}>
      <ComboboxControl {...(control as ControlProps)} />
    </FormField>
  );
}
